import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PushNotificationService, PushNotificationConfig, SubscriptionStatus } from '../../../core/services/push-notification.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-push-notification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatExpansionModule,
    MatDividerModule
  ],
  templateUrl: './push-notification.component.html',
  styleUrls: ['./push-notification.component.scss']
})
export class PushNotificationComponent implements OnInit, OnDestroy {
  @Input() config?: PushNotificationConfig;
  @Input() showTestingTools = true;
  @Input() showDebugInfo = true;
  @Input() title = 'Push Notifications';

  subscriptionStatus: SubscriptionStatus = {
    isSubscribed: false,
    subscription: null,
    permission: 'default',
    serviceWorkerReady: false,
    vapidConfigured: false
  };

  testMessage = '';
  isLoading = false;
  diagnosticInfo: any = null;
  
  private destroy$ = new Subject<void>();

  constructor(private pushNotificationService: PushNotificationService) {}  async ngOnInit(): Promise<void> {
    // Initialize the service with config or environment defaults
    const serviceConfig: PushNotificationConfig = this.config || {
      vapidPublicKey: environment.vapidPublicKey || '',
      backendUrl: environment.apiUrl || ''
    };

    this.pushNotificationService.initialize(serviceConfig);

    // Subscribe to status changes
    this.pushNotificationService.subscriptionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.subscriptionStatus = status;
        this.isLoading = false;
      });

    // Load initial diagnostic info
    await this.loadDiagnosticInfo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async subscribe(): Promise<void> {
    this.isLoading = true;
    try {
      const result = await this.pushNotificationService.requestPermissionAndSubscribe();
      if (!result.success && result.error) {
        console.error('Failed to subscribe:', result.error);
      }
      await this.loadDiagnosticInfo();
    } catch (error) {
      console.error('Error during subscription:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async unsubscribe(): Promise<void> {
    this.isLoading = true;
    try {
      await this.pushNotificationService.unsubscribe();
      await this.loadDiagnosticInfo();
    } catch (error) {
      console.error('Error during unsubscription:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async sendTestNotification(): Promise<void> {
    if (this.testMessage.trim()) {
      this.isLoading = true;
      try {
        const success = await this.pushNotificationService.sendTestNotification(this.testMessage);
        if (success) {
          this.testMessage = '';
        }
      } catch (error) {
        console.error('Error sending test notification:', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
  async loadDiagnosticInfo(): Promise<void> {
    try {
      console.log('Loading diagnostic info...');
      this.diagnosticInfo = await this.pushNotificationService.getDiagnosticInfo();
      console.log('Diagnostic info loaded:', this.diagnosticInfo);
    } catch (error) {
      console.error('Error loading diagnostic info:', error);
      // Set a fallback diagnostic info to show error state
      this.diagnosticInfo = {
        browserCapabilities: {
          serviceWorkerSupported: 'serviceWorker' in navigator,
          pushManagerSupported: 'PushManager' in window,
          notificationSupported: 'Notification' in window
        },
        configuration: {
          vapidPublicKey: 'Error loading',
          backendUrl: 'Error loading'
        },
        serviceWorker: {
          registrationState: 'Error',
          scriptUrl: undefined
        },
        subscription: null,
        status: {
          supported: false,
          permission: 'default',
          subscribed: false,
          serviceWorkerReady: false,
          vapidConfigured: false
        }
      };
    }
  }  testServiceWorkerNotification(): void {
    try {
      console.log('[Component] Sending TEST_NOTIFICATION message to service worker');
      this.pushNotificationService.sendMessageToServiceWorker({
        type: 'TEST_NOTIFICATION'
      });
      // Note: The notification should appear if permission is granted
    } catch (error) {
      console.error('Error testing service worker notification:', error);
    }
  }
  testManualNotification(): void {
    console.log('[Component] Testing manual notification, permission:', Notification.permission);
    if (Notification.permission === 'granted') {
      new Notification('Manual Test Notification', {
        body: 'This is a manual test notification (not from service worker)',
        icon: '/assets/icons/icon-blue-192x192.png'
      });
      console.log('[Component] Manual notification created');
    } else {
      console.warn('[Component] Notification permission not granted:', Notification.permission);
      // Try to request permission if it's default
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          console.log('[Component] Permission after request:', permission);
          if (permission === 'granted') {
            this.testManualNotification();
          }
        });
      }
    }
  }
  forceServiceWorkerUpdate(): void {
    console.log('[Component] Sending SKIP_WAITING message to service worker');
    this.pushNotificationService.sendMessageToServiceWorker({
      type: 'SKIP_WAITING'
    });
    console.log('[Component] Service worker should skip waiting and become active');
  }

  async refreshStatus(): Promise<void> {
    this.isLoading = true;
    try {
      await this.pushNotificationService.refreshStatus();
      await this.loadDiagnosticInfo();
    } catch (error) {
      console.error('Error refreshing status:', error);
    } finally {
      this.isLoading = false;
    }
  }

  toggleDebugInfo(): void {
    this.showDebugInfo = !this.showDebugInfo;
    if (this.showDebugInfo && !this.diagnosticInfo) {
      this.loadDiagnosticInfo();
    }
  }

  async forceDiagnosticRefresh(): Promise<void> {
    this.isLoading = true;
    await this.loadDiagnosticInfo();
    this.isLoading = false;
  }

  getStatusColor(): string {
    switch (this.subscriptionStatus.permission) {
      case 'granted':
        return this.subscriptionStatus.isSubscribed ? 'primary' : 'accent';
      case 'denied':
        return 'warn';
      default:
        return 'basic';
    }
  }

  getStatusText(): string {
    if (this.subscriptionStatus.error) {
      return `Error: ${this.subscriptionStatus.error}`;
    }

    switch (this.subscriptionStatus.permission) {
      case 'granted':
        return this.subscriptionStatus.isSubscribed ? 'Subscribed' : 'Not Subscribed';
      case 'denied':
        return 'Permission Denied';
      case 'default':
        return 'Permission Not Requested';
      default:
        return 'Unknown Status';
    }
  }

  get canSubscribe(): boolean {
    return this.subscriptionStatus.permission !== 'denied' && !this.subscriptionStatus.isSubscribed;
  }

  get canUnsubscribe(): boolean {
    return this.subscriptionStatus.isSubscribed;
  }

  get canSendNotification(): boolean {
    return this.subscriptionStatus.isSubscribed && this.testMessage.trim().length > 0;
  }

  get hasValidVapidKey(): boolean {
    return this.subscriptionStatus.vapidConfigured === true;
  }

  get isServiceWorkerReady(): boolean {
    return this.subscriptionStatus.serviceWorkerReady === true;
  }

  formatEndpoint(endpoint: string): string {
    if (!endpoint) return '';
    return endpoint.length > 60 ? `${endpoint.slice(0, 60)}...` : endpoint;
  }

  formatJsonProperty(obj: any, property: string): string {
    if (!obj || !obj[property]) return 'N/A';
    const value = obj[property];
    return typeof value === 'string' && value.length > 50 ? `${value.slice(0, 50)}...` : value;
  }
}
